import { ApiChartAttribute } from "@incendium/api";
import { ProductPerformanceTab } from "features/products/utils/productPerformanceTabs";
import { Link as MuiLink, Typography } from "@mui/material";
import { ClickableTooltip } from "Components/ClickableTooltip";
import { StyledHelp } from "Components/ClickableTooltip/StyledHelp";
import ProductAnalyticsCard from "features/products/components/ProductAnalyticsCard";
import { useTopPerformingProducts } from "features/products/hooks/useTopPerformingProducts";
import { useProducts } from "Hooks/useProducts";
import { useSelectedProject } from "Hooks";
import { RouteNames } from "Hooks/routeNames";
import useProtectedRoutes from "Hooks/useProtectedRoutes";
import { useCallback } from "react";
import { Link, useHistory } from "react-router-dom";
import { CarouselTitleRow } from "Components/UI/CarouselTrack";
import CarouselCard from "Components/UI/CarouselCard";

interface ProductAnalyticsCarouselProps {
  chartAttributes?: ApiChartAttribute[];
  tab?: ProductPerformanceTab;
  showAllProductsLink?: boolean;
}

function ProductAnalyticsCarousel({
  chartAttributes = [],
  tab = "top_sellers",
  showAllProductsLink = true,
}: ProductAnalyticsCarouselProps) {
  const history = useHistory();
  const { selectedProject, selectedClient } = useSelectedProject();
  const { pathByName } = useProtectedRoutes();
  const { products } = useProducts();
  const {
    products: topProducts,
    loading,
    tabConfig,
  } = useTopPerformingProducts(chartAttributes, tab);

  const onProductClick = useCallback(
    (productId?: number) => {
      if (!productId || !selectedProject?.id || !selectedClient?.id) {
        return;
      }

      history.push(
        pathByName(RouteNames.PRODUCT_DEEP_DIVE, {
          clientId: selectedClient.id,
          projectId: selectedProject.id,
        }),
        { selectedProductId: productId },
      );
    },
    [history, pathByName, selectedClient?.id, selectedProject?.id],
  );

  const productsOverviewPath =
    selectedClient?.id && selectedProject?.id
      ? pathByName(RouteNames.PRODUCTS_OVERVIEW, {
          clientId: selectedClient.id,
          projectId: selectedProject.id,
        })
      : "#";

  return (
    <>
      <CarouselCard
        inview={6}
        itemMinWidth={250}
        title={
          <CarouselTitleRow>
            <Typography variant="subtitle1" fontWeight={700}>
              {tabConfig.carouselTitle}
            </Typography>
            <ClickableTooltip
              text={tabConfig.carouselTooltip}
              placement="right"
              icon={<StyledHelp fontSize="small" />}
            />
          </CarouselTitleRow>
        }
        emptyMessage={"No products available"}
        items={topProducts.map((product) => (
          <ProductAnalyticsCard
            product={product}
            products={products}
            trendMetric={tabConfig.trendMetric}
            leadMetric={tabConfig.leadMetric}
            onClick={onProductClick}
            fullWidth
          />
        ))}
        loading={loading}
        actionSlot={
          showAllProductsLink && (
            <MuiLink
              component={Link}
              to={productsOverviewPath}
              underline="hover"
              sx={{ fontWeight: 600, fontSize: 14, whiteSpace: "nowrap" }}
            >
              View All Products →
            </MuiLink>
          )
        }
      />
    </>
  );
}

export default ProductAnalyticsCarousel;
